import BaseModal from 'components/Modals/BaseModal';
import { usePerms } from 'contexts/PermsContext';
import { DeviceContext } from 'features/integrations/contexts/DeviceContext';
import {
  selectIntegrationFetching,
  selectVisibleDeviceTypes,
} from 'features/integrations/state/selectors/integrationSelectors';
import { canViewDeviceType } from 'features/integrations/utils/integrationVisibilityUtils';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import ExportModal from './ExportModal';

export default function DeviceQueryControl({
  __setStartTime = () => {},
  __setEndTime = () => {},
  setShowCurrentDevice = () => {},
}) {
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const fetching = useSelector(selectIntegrationFetching);
  const perms = usePerms();
  const deviceContext = useContext(DeviceContext);
  const getDeviceData = deviceContext?.getDeviceData || (async () => {});
  // Default to 1 day (minutes)
  const [timeMultiplier, setTimeMultiplier] = useState(1440);
  const [startTime, _setStartTime] = useState(new Date().getTime() - 24 * 60 * 60 * 1000);
  const [endTime, _setEndTime] = useState(new Date().getTime());
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showAdvancedDate, setShowAdvancedDate] = useState(false);
  const [customTimeInputs, setCustomTimeImputs] = useState({});

  const setStartTime = (value) => {
    __setStartTime(value);
    _setStartTime(value);
  };

  const setEndTime = (value) => {
    __setEndTime(value);
    _setEndTime(value);
  };

  const formatDateTimeLocal = (date) => {
    const d = new Date(date);

    const pad = (num) => String(num).padStart(2, '0');

    const formattedDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

    return formattedDate;
  };

  const DAY_IN_MS = 24 * 60 * 60 * 1000;

  const handleSubmit = async () => {
    // For queries longer than 24 hours, request server to check against node timestamps to allow larger ranges
    const useNodeTs = endTime - startTime > DAY_IN_MS;

    // Update parent and local UI immediately so selected timeframe is reflected without waiting for network
    setStartTime(startTime);
    setEndTime(endTime);
    setShowCurrentDevice(false);

    setLoadingMessage('Searching...');
    setSuccessMessage('');
    try {
      const res = await getDeviceData(startTime, endTime, useNodeTs);
      if (res && res.cached) {
        setSuccessMessage('Using cached data for requested range');
      } else {
        setSuccessMessage('Data loaded');
      }
    } catch (err) {
      console.error('Error fetching device data:', err);
      setSuccessMessage('');
      alert('Failed to retrieve device data.');
    } finally {
      setLoadingMessage('');
    }
  };

  return (
    <div className="container-fluid p-0">
      {canViewDeviceType({ system_type: 'Flood' }, perms, visibleDeviceTypes) && (
        <div className="d-flex flex-column gap-2 bg-primary-shadow rounded-2 p-2 w-100">
          <div className="d-flex justify-content-between gap-2">
            <select
              className="form-select flex-fill"
              value={timeMultiplier}
              onChange={(e) => {
                if (e.target.value === '-1') {
                  return;
                }
                if (e.target.value === '0') {
                  setTimeMultiplier(parseInt(e.target.value));
                  setShowAdvancedDate(true);
                } else {
                  setTimeMultiplier(parseInt(e.target.value));
                  setShowAdvancedDate(false);
                  setStartTime(Date.now() - parseInt(e.target.value) * 60000);
                  setEndTime(Date.now());
                }
              }}
            >
              <option value="-1">Time Period</option>
              <option value="0">Custom</option>
              <option value="1">1 Minute</option>
              <option value="2">2 Minutes</option>
              <option value="5">5 Minutes</option>
              <option value="10">10 Minutes</option>
              <option value="15">15 Minutes</option>
              <option value="30">30 Minutes</option>
              <option value="60">1 Hour</option>
              <option value="120">2 Hours</option>
              <option value="180">3 Hours</option>
              <option value="240">4 Hours</option>
              <option value="300">5 Hours</option>
              <option value="600">10 Hours</option>
              <option value="720">12 Hours</option>
              <option value="1440">1 Day</option>
              <option value="10080">7 Days</option>
              <option value="43200">30 Days</option>
            </select>
            <select className="form-select flex-fill">
              <option value="0">Data Aggregation</option>
            </select>
          </div>

          <BaseModal
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
            title="Export Device Data"
          >
            <ExportModal
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              formatDateTimeLocal={formatDateTimeLocal}
              setShowExportModal={setShowExportModal}
            />
          </BaseModal>
          {showAdvancedDate && (
            <div className="d-flex flex-column">
              <label className="text-center">Time Period</label>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-baseline">
                <label className="flex-fill flex-basis-0">Date From</label>
                <input
                  className="form-control flex-fill flex-basis-0"
                  type="datetime-local"
                  value={customTimeInputs.start || formatDateTimeLocal(Date.now())}
                  onChange={(e) => {
                    setCustomTimeImputs({
                      ...customTimeInputs,
                      start: e.target.value,
                    });

                    setStartTime(new Date(e.target.value).getTime());
                  }}
                />
              </div>
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-baseline">
                <label className="flex-fill flex-basis-0">Date to</label>
                <input
                  className="form-control flex-fill flex-basis-0"
                  type="datetime-local"
                  value={customTimeInputs.end || formatDateTimeLocal(Date.now())}
                  onChange={(e) => {
                    setCustomTimeImputs({
                      ...customTimeInputs,
                      end: e.target.value,
                    });

                    setEndTime(new Date(e.target.value).getTime());
                  }}
                />
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between">
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowExportModal(true);
              }}
            >
              Export
            </button>
            <div className="d-flex gap-2">
              <div className="text-success">{!fetching && successMessage}</div>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  // Reset to 1 day (our initial cached range)
                  setTimeMultiplier(1440);
                  setShowAdvancedDate(false);
                  // Update timeframe to 1 day immediately
                  const now = Date.now();
                  setStartTime(now - 1440 * 60000);
                  setEndTime(now);
                  handleSubmit();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loadingMessage}
                className="btn btn-primary"
                onClick={() => {
                  if (!loadingMessage) {
                    handleSubmit();
                  }
                }}
              >
                Search
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
